import { lazy, Suspense, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router";
import {
  ChevronDown,
  CircleUserRound,
  Copyright,
  FileCheck2,
  Info,
  LogOut,
  MessageSquareHeart,
} from "lucide-react";
import { Button } from "@/components/shadcn/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { auth as auth } from "@/integrations/firebase/auth";
import { signOut, User } from "firebase/auth";
import { useAuth } from "@/context/auth-context";
import { FeedbackProvider } from "@/context/FeedbackContext";
import { FOCUS_REPORT_FEEDBACK_EVENT } from "@/components/feedback/feedback-bar";

// Code-split: the global feedback dialog renders only after the user clicks
// "Opinia", so the cold load never pays for it. Mounted only while open.
const AppFeedbackDialog = lazy(() =>
  import("@/components/feedback/app-feedback-dialog").then((m) => ({
    default: m.AppFeedbackDialog,
  })),
);

interface Props {
  user: User | null;
}

const Layout = ({ user }: Props) => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // On a report page the report's rating card is the right target — scroll to
  // it and let the card pulse its highlight. Elsewhere, open the global dialog.
  const handleFeedbackClick = () => {
    if (document.getElementById("report-feedback")) {
      window.dispatchEvent(new CustomEvent(FOCUS_REPORT_FEEDBACK_EVENT));
    } else {
      setFeedbackOpen(true);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/");
      console.log("Signed out successfully");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 w-full max-w-4xl items-center justify-between px-4 sm:px-6">
          {/* The wordmark doubles as the "home" link. NN/g: users miss the
              convention when the logo reads as plain text — so on hover it gets
              the universal link signal (solid underline), a tooltip names the
              destination for mouse users, and AT gets a real accessible name. */}
          <Link
            to="/"
            title="Strona główna"
            aria-label="Chariot, strona główna"
            className="flex items-center gap-2 rounded-sm no-underline underline-offset-4 transition-opacity hover:underline hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="font-serif text-xl font-semibold tracking-tight text-foreground">
              Chariot
            </span>
          </Link>
          <div className="flex items-center gap-3">
            {!user ? (
              <>
                <Link to="/login" className="no-underline">
                  <Button variant="outline" size="sm">
                    Zaloguj się
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleFeedbackClick}
                  className="gap-1.5 border-brand/40 text-brand hover:bg-brand/[0.06] hover:text-brand"
                >
                  <MessageSquareHeart className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Opinia</span>
                  <span className="sr-only sm:hidden">Przekaż opinię</span>
                </Button>
                {admin && (
                  <>
                    <Link to="/admin/drugs" className="no-underline">
                      <Button variant="ghost" size="sm">
                        Leki
                      </Button>
                    </Link>
                    <Link to="/feedback" className="no-underline">
                      <Button variant="ghost" size="sm">
                        Opinie
                      </Button>
                    </Link>
                    <Link to="/admin/doctors" className="no-underline">
                      <Button variant="ghost" size="sm">
                        Lekarze
                      </Button>
                    </Link>
                  </>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5"
                      aria-label="Konto"
                    >
                      <CircleUserRound className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">Konto</span>
                      <ChevronDown
                        className="h-3.5 w-3.5 text-muted-foreground"
                        aria-hidden="true"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-60">
                    <DropdownMenuLabel className="font-normal">
                      <p className="text-xs text-muted-foreground">
                        Zalogowano jako
                      </p>
                      <p className="truncate text-sm font-medium text-foreground">
                        {user.email ?? "—"}
                      </p>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link
                        to="/documents"
                        className="cursor-pointer no-underline hover:no-underline"
                      >
                        <FileCheck2 aria-hidden="true" />
                        Dokumenty i zgody
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => void handleSignOut()}
                      className="cursor-pointer"
                    >
                      <LogOut aria-hidden="true" />
                      Wyloguj się
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Layout-level provider so the global "Opinia" dialog can write feedback
          on pages without a report. On report pages the report tree re-provides
          with the drug/version context, so its feedback stays version-tagged. */}
      <FeedbackProvider>
        <main className="flex-1">
          <div className="mx-auto w-full max-w-4xl px-4">
            <Outlet />
          </div>
        </main>

        {user && feedbackOpen && (
          <Suspense fallback={null}>
            <AppFeedbackDialog
              open={feedbackOpen}
              onOpenChange={setFeedbackOpen}
            />
          </Suspense>
        )}
      </FeedbackProvider>

      <footer className="mt-12 border-t border-border bg-card/40">
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 py-6 sm:px-6 sm:py-8">
          <p className="flex max-w-2xl items-start gap-2 text-sm leading-relaxed text-muted-foreground">
            <Info
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/80"
              aria-hidden="true"
            />
            <span>
              Chariot jest przeznaczony dla osób wykonujących zawody medyczne.
              Raporty mogą zawierać błędy.
            </span>
          </p>
          <div className="flex flex-col gap-3 border-t border-border/70 pt-4 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <p className="flex items-center">
              <Copyright
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground/80 mr-2"
                aria-hidden="true"
              />
              {new Date().getFullYear()} Chariot Medical
            </p>
            <div className="flex gap-4">
              <Link
                to="/terms"
                className="hover:text-foreground hover:underline"
              >
                Warunki korzystania i informacja o AI
              </Link>
              <Link
                to="/privacy"
                className="hover:text-foreground hover:underline"
              >
                Polityka prywatności
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
